import { CheckIcon, CircleCheckIcon, CircleSubtractIcon } from "@xola/icons";
import { Button, Spinner } from "@xola/ui-kit";
import clsx from "clsx";
import React, { ButtonHTMLAttributes } from "react";
import { ExperiencePurchaseItem, GuestStatus } from "types";
import { NoTranslate } from "../../Common/NoTranslate";
import { getGuestsByStatus, isItemLevelCheckIn } from "./CheckInHelper";

interface CheckInButtonWithSummaryProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    purchaseItem: ExperiencePurchaseItem;
    isLoading: boolean;
    disabled: boolean;
    action: "checkIn" | "noShow";
}

export const CheckInButtonWithSummary = ({
    purchaseItem,
    isLoading,
    disabled,
    action = "checkIn",
    ...rest
}: CheckInButtonWithSummaryProps) => {
    const guestStatusMap: { [key in GuestStatus]: number } = getGuestsByStatus(purchaseItem);
    // Item-level check-in (no guests) counts the item as one unit. XS-1821.
    const totalGuestsCount = isItemLevelCheckIn(purchaseItem) ? 1 : purchaseItem.guests.length;
    const guestStatusCount =
        action === "checkIn"
            ? guestStatusMap[GuestStatus.ARRIVED] + guestStatusMap[GuestStatus.RETURNED]
            : guestStatusMap[GuestStatus.NO_SHOW];

    const isNotCheckedIn = guestStatusMap[GuestStatus.ARRIVED] + guestStatusMap[GuestStatus.RETURNED] === 0;
    const isFullyCheckedIn =
        guestStatusMap[GuestStatus.ARRIVED] + guestStatusMap[GuestStatus.RETURNED] === totalGuestsCount;
    const isPartiallyCheckedIn = !isNotCheckedIn && !isFullyCheckedIn;

    const perGuestCheckInStatusIcon = isNotCheckedIn ? (
        <CircleCheckIcon className="mt-0.25" />
    ) : isFullyCheckedIn ? (
        <CheckIcon className="rounded-full bg-green-dark p-0.5 pb-0.75 text-white" />
    ) : (
        <span className="mb-0.5 h-3 w-3 rounded-full bg-warning text-xs text-white">!</span>
    );

    return (
        <Button
            color="secondary"
            variant="outline"
            size="small"
            className={clsx(
                "mr-1",
                isPartiallyCheckedIn && !isLoading && action === "checkIn" ? "relative bottom-1" : null,
            )}
            disabled={disabled}
            data-testid={
                action === "checkIn" ? "dashboard-search-item-check-in-button" : "dashboard-search-item-no-show-button"
            }
            {...rest}
        >
            {isLoading ? (
                <Spinner size="tiny" />
            ) : action === "checkIn" ? (
                perGuestCheckInStatusIcon
            ) : (
                <CircleSubtractIcon className="text-danger" />
            )}
            {!isLoading && (
                <NoTranslate className="ml-1">
                    {guestStatusCount}/{totalGuestsCount}
                </NoTranslate>
            )}
        </Button>
    );
};
